import { createContext } from 'react';

/**
 * How much information fits on screen. `comfortable` is the touch-friendly
 * default; `compact` and `dense` tighten tables, lists, cards and page gaps
 * for data-heavy work.
 */
export type Density = 'comfortable' | 'compact' | 'dense';

export type DensityPreference = Density | 'auto';

export const DENSITIES: Density[] = ['comfortable', 'compact', 'dense'];

export interface DensityContextValue {
  /** The density in effect (resolved from the preference and screen size). */
  density: Density;
  preference: DensityPreference;
  setPreference: (preference: DensityPreference) => void;
}

export const DensityContext = createContext<DensityContextValue | undefined>(undefined);

/** Spacing that scales with density, in MUI spacing units (1 = 8px). */
export interface DensityTokens {
  /** Table/list row vertical padding. */
  rowPaddingY: number;
  /** Card and panel inner padding. */
  cardPadding: number;
  /** Gap between major page sections. */
  sectionGap: number;
  /** IconButton inset. */
  iconButtonPadding: number;
}

export const DENSITY_TOKENS: Record<Density, DensityTokens> = {
  comfortable: { rowPaddingY: 1, cardPadding: 2, sectionGap: 2, iconButtonPadding: 0.75 },
  compact: { rowPaddingY: 0.5, cardPadding: 1.5, sectionGap: 1.5, iconButtonPadding: 0.5 },
  dense: { rowPaddingY: 0.25, cardPadding: 1, sectionGap: 1, iconButtonPadding: 0.25 },
};
